import React, { useEffect, useState, useCallback } from 'react';
import { courseAPI } from '../api/courseApi';
import { useAuth } from '../context/AuthContext';
import { getImageUrl } from '../utils/helpers';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';

const MyPurchased = () => {
  const [purchasedCourses, setPurchasedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { userInfo } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPurchasedCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPurchasedCourses = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      
      const userId = userInfo?.user_detail?.id;
      
      if (!userId) {
        toast.error('Please login to view your courses');
        navigate('/login');
        return;
      }

      // Using the same endpoint as mobile app: /getMypurchase/{userId}
      const response = await courseAPI.getMyPurchased(userId);
      
      if (response && response.data) {
        setPurchasedCourses(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching purchased courses:', error);
      toast.error(error.response?.data?.message || 'Failed to load purchased courses');
    } finally {
      setLoading(false);
      if (isRefresh) setRefreshing(false);
    }
  };

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchPurchasedCourses(true);
  }, []);

  const handleSubjectClick = useCallback((course) => {
    // Navigate to Subjects page (matching mobile app Subject screen)
    // Pass the type which determines if it's 2-screen flow (old) or 4-screen flow (new)
    navigate(`/subjects/${course.id}`, {
      state: {
        type: course.type,  // 'new' = 4-screen flow, 'old' = 2-screen flow
        id: course.id,
        subject: course.sub_name,
        flag: 0, // Matching mobile app - 0 means purchased view
        viewType: course.viewType, // Pass additional viewType if available
      }
    });
  }, [navigate]);

  const handleCourseDetails = useCallback((course) => {
    navigate(`/course/${course.id}`, {
      state: { course, showDescription: true }
    });
  }, [navigate]);

  const openGroupLink = useCallback((url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.info('Group link not available');
    }
  }, []);

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12 sm:py-16">
        <div className="container">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">My Purchased Courses</h1>
              <p className="text-white/90 text-base sm:text-lg">Access all your enrolled courses</p>
            </div>
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-white/20 hover:bg-white/30 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition disabled:opacity-50 flex items-center gap-2"
            >
              <span className={refreshing ? 'animate-spin' : ''}>🔄</span>
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Purchased Courses Section */}
      <div className="py-8 sm:py-12 lg:py-16">
        <div className="container">
          {purchasedCourses.length > 0 ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 sm:mb-8">
                <p className="text-gray-700 text-sm sm:text-base">
                  📚 You have <span className="font-bold text-blue-600">{purchasedCourses.length}</span> purchased course{purchasedCourses.length !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {purchasedCourses.map((course) => (
                  <div
                    key={course.id}
                    className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    {/* Course Image */}
                    {course.images && (
                      <div className="relative h-48 overflow-hidden bg-gray-200">
                        <img 
                          src={getImageUrl(course.images)}
                          alt={course.sub_name}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x300?text=Course+Image';
                          }}
                        />
                        <div className="absolute top-3 right-3">
                          <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                            ✓ Purchased
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Course Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                        {course.sub_name}
                      </h3>

                      {/* Price Display */}
                      {course.price && (
                        <div className="flex items-center gap-2 mb-4">
                          {course.mrp && (
                            <span className="text-gray-400 line-through text-sm">
                              ₹{course.mrp}
                            </span>
                          )}
                          <span className="text-green-600 text-xl font-bold">
                            ₹{course.price}
                          </span>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="space-y-3">
                        <button
                          onClick={() => handleSubjectClick(course)}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition"
                        >
                          View Subjects
                        </button>
                        
                        <div className="flex gap-2">
                          {course.group_url ? (
                            <button
                              onClick={() => openGroupLink(course.group_url)}
                              className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 px-4 rounded-lg transition text-sm"
                            >
                              Join Group
                            </button>
                          ) : (
                            <button
                              onClick={() => handleCourseDetails(course)}
                              className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2.5 px-4 rounded-lg transition text-sm"
                            >
                              Details
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer Info */}
              <div className="mt-8 text-center text-gray-500 text-sm">
                <p>Total: {purchasedCourses.length} course{purchasedCourses.length > 1 ? 's' : ''}</p>
              </div>
            </>
          ) : (
            <div className="text-center py-16 sm:py-24">
              <div className="text-6xl sm:text-8xl mb-6">📚</div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-700 mb-3">
                No Purchased Courses Yet
              </h3>
              <p className="text-gray-500 text-base sm:text-lg mb-8 max-w-md mx-auto">
                Start learning by purchasing your first course
              </p>
              <Link to="/courses" className="btn btn-primary btn-large inline-block">
                Browse Courses →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyPurchased;
